import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import AnnouncementBar from '../components/AnnouncementBar';
import api from '../services/api';
import toast from '../utils/toast';
import RotatingQrImg from '../components/RotatingQrImg';
import AnimatedQrFrame from '../components/AnimatedQrFrame';
import './UserDashboardV2.css';

const VOIDED_STATUSES = new Set(['REFUNDED', 'CANCELLED', 'VOIDED']);
const ACTIVE_STATUS = 'ISSUED';
const DASHBOARD_PASS_PREVIEW_LIMIT = 3;
const TICKET_TABS = ['all', 'festival', 'amp', 'vendor', 'parking', 'other'];
const CATEGORY_LABELS = {
  all: 'All',
  festival: 'Festival Pass',
  amp: 'Concert Seat',
  vendor: 'Vendor Booth',
  parking: 'Parking Pass',
  other: 'Access Pass',
};
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
  { id: 'tickets', label: 'Tickets', icon: TicketIcon },
  { id: 'orders', label: 'Orders', icon: OrderIcon },
  { id: 'transfers', label: 'Transfer History', icon: TransferIcon },
  { id: 'affiliate', label: 'Affiliate', icon: AffiliateIcon },
  { id: 'payment', label: 'Payment', icon: CardIcon },
  { id: 'account', label: 'Account', icon: AccountIcon },
];

function isVoided(ticket) {
  return VOIDED_STATUSES.has(ticket?.status);
}

function TransferHistoryTab({ transferHistory, onBack }) {
  const sentTransfers = Array.isArray(transferHistory?.sent) ? transferHistory.sent : [];
  const receivedTransfers = Array.isArray(transferHistory?.received) ? transferHistory.received : [];

  return (
    <div className="udv2-tab-content fade-in">
      <BackButton onClick={onBack} />
      <div className="udv2-page-header">
        <h2>Transfer history</h2>
        <p>{sentTransfers.length + receivedTransfers.length} records</p>
      </div>

      {(sentTransfers.length === 0 && receivedTransfers.length === 0) ? (
        <div className="udv2-empty"><TransferIcon /><p>No transfer history yet.</p></div>
      ) : (
        <div className="udv2-transfer-history-grid">
          {receivedTransfers.length > 0 && (
            <section className="udv2-section">
              <div className="udv2-section-header">
                <h2>Received</h2>
              </div>
              <div className="udv2-list">
                {receivedTransfers.map((transfer) => {
                  const seatLabel = formatTransferSeatLabel(transfer);
                  return (
                    <div key={`received-${transfer.id}`} className="udv2-list-row">
                      <div>
                        <div className="udv2-list-title">{transfer.ticket_type || 'Ticket transfer'}</div>
                        <div className="udv2-list-meta">From: {transfer.from_user_email || '—'}</div>
                        {transfer.ticket_code && <div className="udv2-list-meta">Code: {transfer.ticket_code}</div>}
                        {seatLabel && <div className="udv2-list-meta">{seatLabel}</div>}
                        <div className="udv2-list-meta">{formatTransferTimeline(transfer)}</div>
                      </div>
                      <div className="udv2-list-end">
                        <span className={`udv2-pill ${transferStatusColor(transfer.status)}`}>{transfer.status || '—'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {sentTransfers.length > 0 && (
            <section className="udv2-section">
              <div className="udv2-section-header">
                <h2>Sent</h2>
              </div>
              <div className="udv2-list">
                {sentTransfers.map((transfer) => {
                  const seatLabel = formatTransferSeatLabel(transfer);
                  return (
                    <div key={`sent-${transfer.id}`} className="udv2-list-row">
                      <div>
                        <div className="udv2-list-title">{transfer.ticket_type || 'Ticket transfer'}</div>
                        <div className="udv2-list-meta">To: {transfer.to_email || '—'}</div>
                        {transfer.ticket_code && <div className="udv2-list-meta">Code: {transfer.ticket_code}</div>}
                        {seatLabel && <div className="udv2-list-meta">{seatLabel}</div>}
                        <div className="udv2-list-meta">{formatTransferTimeline(transfer)}</div>
                      </div>
                      <div className="udv2-list-end">
                        <span className={`udv2-pill ${transferStatusColor(transfer.status)}`}>{transfer.status || '—'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function formatCents(cents) {
  const n = Number(cents || 0);
  return `$${(n / 100).toFixed(2)}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function statusColor(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'issued') return 'success';
  if (s === 'used') return 'neutral';
  if (s === 'pending' || s === 'transfer_pending') return 'warning';
  if (s === 'refunded' || s === 'cancelled' || s === 'voided' || s === 'disputed') return 'danger';
  return 'neutral';
}

function transferStatusColor(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'accepted') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'cancelled' || s === 'expired' || s === 'returned' || s === 'rejected') return 'danger';
  return 'neutral';
}

function formatTransferTimeline(transfer) {
  if (!transfer) return '—';
  if (transfer.accepted_at) return `Accepted ${formatDateTime(transfer.accepted_at)}`;
  if (transfer.status === 'PENDING' && transfer.expires_at) return `Expires ${formatDateTime(transfer.expires_at)}`;
  if (transfer.created_at) return `Sent ${formatDateTime(transfer.created_at)}`;
  return '—';
}

function formatTransferSeatLabel(transfer) {
  const details = transfer?.ticket_details || {};
  const bits = [];
  if (details.event_date) bits.push(formatDate(details.event_date));
  if (details.section) bits.push(`Section ${details.section}`);
  if (details.row) bits.push(`Row ${details.row}`);
  if (details.seat) bits.push(`Seat ${details.seat}`);
  return bits.join(' · ');
}

function getCategoryLabel(group) {
  return CATEGORY_LABELS[group] || group;
}

function getTicketGroup(ticket, activeFilter) {
  const type = String(ticket.ticket_type_name || ticket.metadata?.ticket_name || '').toLowerCase();
  const slug = String(ticket.ticket_type?.slug || '').toLowerCase();
  const meta = ticket.metadata || {};

  if (
    meta.parking_pass_assignment === true ||
    String(meta.fulfillment_type || '').toUpperCase() === 'VIP_PARKING_PASS' ||
    slug === 'vip-parking-pass' ||
    (type.includes('parking') && type.includes('vip'))
  ) return 'parking';

  if (
    meta.type === 'vendor-booth' ||
    ['food', 'bazaar', 'food-truck', 'food-booth'].includes(String(meta.business_type || '').toLowerCase()) ||
    /vendor booth|food truck|food booth|bazaar/.test(type) ||
    /vendor-booth|food-truck|food-booth|bazaar/.test(slug)
  ) return 'vendor';

  if (
    meta.amphitheater_seat === true ||
    meta.section || meta.row || meta.seat ||
    /section|row|seat|premium box|pit|circle|terr|orch/.test(type)
  ) return 'amp';

  if (
    meta.qr_disabled === true ||
    meta.wristband_assignment === true ||
    /wristband/.test(type) ||
    slug.startsWith('ride-wb-')
  ) return 'other';

  return 'festival';
}

function canAccessAdminDashboard(user) {
  const role = String(user?.role || '').trim().toLowerCase();
  return !!(
    user?.is_staff
    || user?.is_superuser
    || role === 'admin'
    || role === 'superadmin'
    || role === 'staff_scanner'
  );
}

function parseSeatValue(value) {
  if (value === null || typeof value === 'undefined') return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const n = Number(raw);
  if (Number.isFinite(n)) return String(n);
  return raw;
}

function getAmphitheaterSeatData(ticket) {
  const amph = ticket?.amphitheater_ticket || {};
  const meta = ticket?.metadata || {};

  const section = String(
    amph.section_name
    || amph.section
    || meta.section_name
    || meta.section
    || ''
  ).trim();
  const row = String(amph.row || meta.row || '').trim();
  const seat = parseSeatValue(amph.seat_number || amph.seat || meta.seat_number || meta.seat);
  const eventDate = String(amph.event_date || meta.event_date || ticket?.event_date || '').trim();

  if (!section && !row && !seat) return null;
  return { section, row, seat, eventDate };
}

function formatSeatLabel(seatData, short = false) {
  if (!seatData) return '';
  const bits = [];
  if (seatData.section) bits.push(`${short ? 'Sec' : 'Section'} ${seatData.section}`);
  if (seatData.row) bits.push(`Row ${seatData.row}`);
  if (seatData.seat) bits.push(`Seat ${seatData.seat}`);
  return bits.join(' · ');
}

function isAmphitheaterOrderItem(item) {
  const name = String(item?.ticket_type_name || item?.name || '').toLowerCase();
  return (
    name.includes('amphitheater')
    || name.includes('pacific')
    || name.includes('pac amp')
  );
}

function normalizeOrderAmpSeats(order) {
  if (!Array.isArray(order?.amphitheater_seats)) return [];
  const seen = new Set();
  return order.amphitheater_seats
    .map((seat) => {
      const section = String(seat?.section || '').trim();
      const row = String(seat?.row || '').trim();
      const seatNumber = parseSeatValue(seat?.seat_number);
      const eventDate = String(seat?.event_date || '').trim();
      const location = seat?.location || formatSeatLabel({ section, row, seat: seatNumber }, false);
      const display = seat?.display || (location ? `Pacific Amphitheatre, ${location}` : 'Pacific Amphitheatre');
      return {
        ...seat,
        section,
        row,
        seat_number: seatNumber,
        event_date: eventDate,
        location,
        display,
      };
    })
    .filter((seat) => seat.section || seat.row || seat.seat_number || seat.display)
    .filter((seat) => {
      const key = [seat.event_date, seat.section, seat.row, seat.seat_number].join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function getFestivalPassKey(ticket) {
  const slug = String(ticket?.ticket_type?.slug || '').toLowerCase();
  if (slug === '1day' || slug.endsWith('-1day')) return '1day';
  if (slug === '2day' || slug.endsWith('-2day')) return '2day';
  if (slug === '3day' || slug.endsWith('-3day')) return '3day';

  const passType = String(ticket?.metadata?.pass_type || ticket?.metadata?.festival_pass_key || '').toLowerCase();
  if (passType === '1day') return '1day';
  if (passType === '2day') return '2day';
  if (passType === '3day') return '3day';

  const haystack = String(ticket?.ticket_type_name || ticket?.metadata?.ticket_name || '').toLowerCase();
  if (haystack.includes('1-day') || haystack.includes('1 day') || haystack.includes('one-day') || haystack.includes('one day')) return '1day';
  if (haystack.includes('2-day') || haystack.includes('2 day') || haystack.includes('two-day') || haystack.includes('two day')) return '2day';
  if (haystack.includes('3-day') || haystack.includes('3 day') || haystack.includes('three-day') || haystack.includes('three day')) return '3day';

  return '';
}

function getTicketEventDate(ticket) {
  return (
    ticket?.event_date
    || ticket?.amphitheater_ticket?.event_date
    || ticket?.metadata?.event_date
    || ticket?.metadata?.valid_for_date
    || null
  );
}

function getTicketDisplayType(ticket) {
  const passKey = getFestivalPassKey(ticket);
  if (passKey === '1day') return '1-Day Pass';
  if (passKey === '2day') return '2-Day Pass';
  if (passKey === '3day') return '3-Day Pass';

  const metadata = ticket?.metadata || {};
  const ticketName = String(ticket?.ticket_type_name || metadata.ticket_name || '').toLowerCase();
  if ((ticket?._group === 'amp') || ticketName.includes('pac amp') || ticketName.includes('amphitheater')) return 'PAC AMP';
  if ((metadata.assignment_source === 'media') || metadata.media_inquiry_id || ticketName.includes('media')) return 'Media';
  if ((metadata.assignment_source === 'vip') || metadata.vip_registration_id || ticketName.includes('vip')) return 'VIP';
  if (ticket?._group === 'vendor') return 'Vendor Pass';
  if (ticket?._group === 'parking') return 'VIP Parking';

  return ticket?.ticket_type_name || metadata.ticket_name || 'Festival Pass';
}

function getFestivalValidityDescription(ticket) {
  const passKey = getFestivalPassKey(ticket);
  if (passKey === '1day') return 'Valid for admission on one festival day only.';
  if (passKey === '2day') return 'Valid for one person on any two festival days.';
  if (passKey === '3day') return 'Valid for one person on all three festival days.';
  return '';
}

function formatValidDaysForDisplay(validDays) {
  if (!Array.isArray(validDays) || validDays.length === 0) return '';
  return validDays.map((day) => formatDate(day)).join(', ');
}

function DashboardIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="2"/><rect x="14" y="3" width="7" height="5" rx="2"/><rect x="14" y="12" width="7" height="9" rx="2"/><rect x="3" y="16" width="7" height="5" rx="2"/></svg>; }
function TicketIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9a3 3 0 0 1 0-6v6Z"/><path d="M22 9a3 3 0 0 0 0-6v6Z"/><path d="M2 15a3 3 0 0 0 0 6v-6Z"/><path d="M22 15a3 3 0 0 1 0 6v-6Z"/><path d="M2 9h20"/><path d="M2 15h20"/></svg>; }
function OrderIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>; }
function TransferIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3l4 4-4 4"/><path d="M3 7h18"/><path d="M7 21l-4-4 4-4"/><path d="M21 17H3"/></svg>; }
function AffiliateIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="3"/><path d="M2 19a7 7 0 0 1 12-5.7"/><circle cx="19" cy="19" r="3"/></svg>; }
function CardIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>; }
function AccountIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>; }
function QrIcon() { return <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M10 3h4"/><path d="M10 21h4"/><path d="M21 10v4"/><path d="M3 10v4"/></svg>; }
function ChevronRight() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>; }
function CloseIcon() { return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>; }
function ArrowRight() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>; }
function ArrowLeft() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>; }
function CopyIcon() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>; }
function LogoutIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>; }
function VendorIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z"/><path d="M3 9V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4"/><path d="M9 13h6"/></svg>; }
function DocIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>; }
function UploadIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>; }

function BackButton({ onClick }) {
  return (
    <button className="udv2-back-btn" onClick={onClick} aria-label="Back to dashboard">
      <ArrowLeft /> <span>Back</span>
    </button>
  );
}

export default function UserDashboardV2() {
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [ticketFilter, setTicketFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const [tickets, setTickets] = useState({});
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [affiliate, setAffiliate] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [savedCard, setSavedCard] = useState(null);
  const [publicConfig, setPublicConfig] = useState(null);
  const [transferHistory, setTransferHistory] = useState({ sent: [], received: [] });
  const [vendorDocuments, setVendorDocuments] = useState([]);
  const [vendorUploading, setVendorUploading] = useState({});
  const [vendorDocError, setVendorDocError] = useState('');
  const [vendorDocSuccess, setVendorDocSuccess] = useState('');
  const [vendorSigning, setVendorSigning] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);


  const allTickets = useMemo(() => {
    const arr = [];
    ['festival_tickets', 'amphitheater_tickets', 'vendor_tickets', 'parking_tickets', 'other_tickets'].forEach((k) => {
      const list = tickets[k] || [];
      arr.push(...list.map((t) => ({ ...t, _group: getTicketGroup(t) })));
    });
    return arr;
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    if (ticketFilter === 'all') return allTickets;
    return allTickets.filter((t) => t._group === ticketFilter);
  }, [allTickets, ticketFilter]);

  const firstName = useMemo(() => {
    const name = user?.full_name || profile?.full_name || user?.email || '';
    return name.split(' ')[0] || name;
  }, [user, profile]);
  const canAccessAdmin = useMemo(() => canAccessAdminDashboard(user), [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const loadData = async () => {
    try {
      const [ticketsRes, ordersRes, profileRes, affiliateRes, payoutsRes, cardRes, configRes, vendorDocsRes, transfersRes] = await Promise.all([
        api.getUserTickets(),
        api.getMyOrders(),
        api.getProfile(),
        api.getMyAffiliateInfo().catch(() => null),
        api.getMyAffiliatePayouts().catch(() => null),
        api.getSavedCard().catch(() => null),
        api.getPublicConfig().catch(() => null),
        api.getVendorDocuments().catch(() => null),
        api.getMyTransfers().catch(() => null),
      ]);

      if (ticketsRes?.success) {
        const d = ticketsRes.data || {};
        const clean = (list) => (Array.isArray(list) ? list.filter((t) => !isVoided(t)) : []);
        setTickets({
          festival_tickets: clean(d.festival_tickets || []),
          amphitheater_tickets: clean(d.amphitheater_tickets || []),
          vendor_tickets: clean(d.vendor_tickets || []),
          parking_tickets: clean(d.parking_tickets || []),
          other_tickets: clean(d.other_tickets || []),
        });
      }
      if (ordersRes?.success) setOrders(ordersRes.data || []);
      if (profileRes?.success) setProfile(profileRes.data);
      if (affiliateRes?.success) setAffiliate(affiliateRes.data);
      if (payoutsRes?.success) setPayouts(payoutsRes.data || []);
      if (cardRes?.success && cardRes.has_card) setSavedCard(cardRes.card || null);
      if (configRes?.success) setPublicConfig(configRes.data || null);
      if (vendorDocsRes?.success) setVendorDocuments(vendorDocsRes.data || []);
      if (transfersRes?.success) {
        setTransferHistory({
          sent: transfersRes.data?.sent || [],
          received: transfersRes.data?.received || [],
        });
      } else {
        setTransferHistory({ sent: [], received: [] });
      }
    } catch (err) {
      console.error('[UserDashboardV2] load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('docusign') === 'complete') {
      const completeDocuSign = async () => {
        try {
          const result = await api.completeDocuSignContract();
          if (result?.success && result?.is_signed) {
            toast.success('Contract signed successfully!');
          } else if (result?.status === 'pending') {
            toast.info('Contract signing not yet completed');
          }
        } catch (err) {
          console.error('[UserDashboardV2] DocuSign complete failed:', err);
        }
        loadVendorDocuments();
        loadData();
      };
      completeDocuSign();
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.delete('docusign');
      window.history.replaceState({}, document.title, nextUrl.pathname + nextUrl.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const activeTickets = useMemo(() => allTickets.filter((t) => String(t.status).toUpperCase() === ACTIVE_STATUS), [allTickets]);
  const totalTickets = allTickets.length;

  const vendorOrders = useMemo(() => orders.filter((o) => {
    const vendorStatus = String(o?.vendor_status || '').toUpperCase();
    const hasVendorBooth = Array.isArray(o?.metadata?.vendor_booths) && o.metadata.vendor_booths.length > 0;
    return hasVendorBooth && ['PENDING', 'APPROVED', 'REJECTED'].includes(vendorStatus);
  }), [orders]);
  const hasVendorOrder = vendorOrders.length > 0;

  const vendorOrderInfo = useMemo(() => {
    if (vendorOrders.length === 0) return null;
    const priority = { PENDING: 0, APPROVED: 1, REJECTED: 2 };
    const active = [...vendorOrders].sort((a, b) => (priority[a.vendor_status] ?? 9) - (priority[b.vendor_status] ?? 9))[0];
    const booth = active.metadata.vendor_booths[0] || {};
    const businessName = booth.legal_name || booth.business_name || booth.booth_name || '';
    const boothSize = booth.booth_size || '10x10';
    const businessType = booth.business_type || '';
    const boothType = booth.booth_type || '';
    const isFoodTruck = boothType.toLowerCase().includes('food-truck') || boothType.toLowerCase().includes('food_truck');
    let boothLabel = '';
    if (isFoodTruck) boothLabel = 'Food/Beverage Truck';
    else if (businessType.toLowerCase().includes('food')) boothLabel = `${boothSize} Food/Beverage Booth`;
    else if (businessType.toLowerCase().includes('bazaar')) boothLabel = `${boothSize} Bazaar Booth`;
    else boothLabel = `${boothSize} Vendor Booth`;
    return {
      status: active.vendor_status,
      businessName,
      orderNumber: active.order_number,
      boothLabel,
      vendorName: active.buyer?.full_name || user?.full_name || '',
      orderId: active.id,
      createdAt: active.created_at,
      totalPaid: Number(active.total || 0),
      licensePlateNumber: booth.license_plate_number || '',
      productType: booth.product_type || '',
      isFoodTruck,
    };
  }, [vendorOrders, user]);

  const loadVendorDocuments = useCallback(async () => {
    try {
      const res = await api.getVendorDocuments();
      if (res?.success) setVendorDocuments(res.data || []);
    } catch (err) {
      console.error('[UserDashboardV2] vendor docs load failed:', err);
    }
  }, []);

  const handleVendorDocUpload = useCallback(async (docType, file) => {
    if (!file) return;
    const docNames = { SELLER_PERMIT: "Seller's Permit", BUSINESS_LICENSE: 'Business License', HEALTH_PERMIT: 'Health Permit', FOOD_HANDLER: "Food Handler's License" };
    const docName = docNames[docType] || docType;
    setVendorUploading((prev) => ({ ...prev, [docType]: true }));
    setVendorDocError('');
    setVendorDocSuccess('');
    try {
      const response = await api.uploadVendorDocument(docType, file);
      if (response?.success) {
        await loadVendorDocuments();
        toast.success(`${docName} uploaded successfully!`);
        setVendorDocSuccess(`${docName} uploaded successfully!`);
        setTimeout(() => setVendorDocSuccess(''), 5000);
      } else {
        const errorMsg = response?.error?.message || 'Failed to upload document';
        toast.error(`Failed to upload ${docName}: ${errorMsg}`);
        setVendorDocError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to upload document';
      toast.error(`Error uploading ${docName}: ${errorMsg}`);
      setVendorDocError(errorMsg);
    } finally {
      setVendorUploading((prev) => ({ ...prev, [docType]: false }));
    }
  }, [loadVendorDocuments]);

  const handleSignContract = useCallback(async () => {
    setVendorSigning(true);
    setVendorDocError('');
    try {
      const returnUrl = `${window.location.origin}/dashboard?docusign=complete`;
      const response = await api.createDocuSignContract(returnUrl);
      if (response?.success && response?.signing_url?.startsWith('https://')) {
        window.location.href = response.signing_url;
        return;
      }
      const errorMsg = response?.error?.message || response?.error || 'Failed to create contract';
      setVendorDocError(`Contract signing unavailable: ${errorMsg}`);
    } catch (err) {
      const errorDetail = err.response?.data?.error?.message || err.message || 'Unknown error';
      setVendorDocError(`Contract signing error: ${errorDetail}. Please contact support.`);
    } finally {
      setVendorSigning(false);
    }
  }, []);

  const vendorTabs = useMemo(() => hasVendorOrder ? [{ id: 'vendor', label: 'Vendor', icon: VendorIcon }] : [], [hasVendorOrder]);

  const stats = useMemo(() => [
    { label: 'Active Tickets', value: activeTickets.length, sub: `${totalTickets} total` },
    { label: 'Orders', value: orders.length, sub: 'lifetime' },
    { label: 'Wallet Ready', value: activeTickets.length, sub: 'passes available' },
    { label: 'Affiliate Code', value: affiliate?.coupon_code || '—', sub: `${affiliate?.commission_percent ?? 10}% back` },
  ], [activeTickets.length, totalTickets, orders.length, affiliate]);

  if (authLoading || loading) {
    return (
      <div className="udv2-root">
        <AnnouncementBar />
        <div className="udv2-page-bg" aria-hidden="true">
          <video
            src="/hero-final.mp4"
            alt="OC MENA Festival"
            className="udv2-page-video"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="udv2-page-overlay" />
        </div>
        <div className="udv2-loading-screen">
          <div className="udv2-loading-card">
            <div className="udv2-loading-spinner" />
            <p>Loading your dashboard…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="udv2-root">
      <AnnouncementBar />
      <div className="udv2-page-bg" aria-hidden="true">
        <video
          src="/hero-final.mp4"
          alt="OC MENA Festival"
          className="udv2-page-video"
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="udv2-page-overlay" />
      </div>

      <div className="udv2-shell">
        <nav className="udv2-sidebar" role="tablist" aria-label="Dashboard navigation">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                className={`udv2-nav-item ${active ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                role="tab"
                aria-selected={active}
              >
                <Icon />
                <span>{tab.label}</span>
                {tab.id === 'tickets' && totalTickets > 0 && <span className="udv2-nav-badge">{totalTickets}</span>}
              </button>
            );
          })}
          {vendorTabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                className={`udv2-nav-item ${active ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                role="tab"
                aria-selected={active}
              >
                <Icon />
                <span>{tab.label}</span>
              </button>
            );
          })}
          <button className="udv2-nav-item udv2-logout-btn" onClick={handleLogout}>
            <LogoutIcon />
            <span>Log out</span>
          </button>
        </nav>
        <main className="udv2-main">
          {activeTab === 'dashboard' && (
            <DashboardTab
              firstName={firstName}
              stats={stats}
              allTickets={allTickets}
              orders={orders}
              onViewTickets={() => setActiveTab('tickets')}
              onViewOrders={() => setActiveTab('orders')}
              onViewTransfers={() => setActiveTab('transfers')}
              onViewAffiliate={() => setActiveTab('affiliate')}
              onViewPayment={() => setActiveTab('payment')}
              onViewAccount={() => setActiveTab('account')}
              onViewAdmin={() => navigate('/hq')}
              onSelectTicket={setSelectedTicket}
              onSelectOrder={setSelectedOrder}
              canAccessAdmin={canAccessAdmin}
              onLogout={handleLogout}
            />
          )}
          {activeTab === 'tickets' && (
            <TicketsTab
              tickets={filteredTickets}
              allTickets={allTickets}
              filter={ticketFilter}
              setFilter={setTicketFilter}
              onSelect={setSelectedTicket}
              onBack={() => setActiveTab('dashboard')}
            />
          )}
          {activeTab === 'orders' && <OrdersTab orders={orders} onSelect={setSelectedOrder} onBack={() => setActiveTab('dashboard')} />}
          {activeTab === 'transfers' && <TransferHistoryTab transferHistory={transferHistory} onBack={() => setActiveTab('dashboard')} />}
          {activeTab === 'affiliate' && <AffiliateTab affiliate={affiliate} payouts={payouts} onBack={() => setActiveTab('dashboard')} />}
          {activeTab === 'payment' && <PaymentTab card={savedCard} setCard={setSavedCard} onBack={() => setActiveTab('dashboard')} />}
          {activeTab === 'account' && <AccountTab profile={profile} setProfile={setProfile} onBack={() => setActiveTab('dashboard')} />}
          {activeTab === 'vendor' && (
            <VendorTab
              vendorOrderInfo={vendorOrderInfo}
              vendorDocuments={vendorDocuments}
              uploading={vendorUploading}
              docError={vendorDocError}
              docSuccess={vendorDocSuccess}
              signing={vendorSigning}
              onUpload={handleVendorDocUpload}
              onSignContract={handleSignContract}
              onBack={() => setActiveTab('dashboard')}
            />
          )}
        </main>
      </div>

      <div className="udv2-section-fade" aria-hidden="true" />
      <SponsorsSection />
      <Footer />
      <ScrollToTop />

      {selectedTicket && (
        <TicketModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onRefresh={loadData}
          googleWalletEnabled={publicConfig?.google_wallet_enabled}
        />
      )}
      {selectedOrder && (
        <OrderModal
          order={selectedOrder}
          currentUserEmail={profile?.email || user?.email || ''}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  );
}

function DashboardTab({ firstName, stats, allTickets, orders, onViewTickets, onViewOrders, onViewTransfers, onViewAffiliate, onViewPayment, onViewAccount, onViewAdmin, onSelectTicket, onSelectOrder, canAccessAdmin, onLogout }) {
  const recentOrders = orders.slice(0, 3);
  const previewTickets = useMemo(() => allTickets.slice(0, DASHBOARD_PASS_PREVIEW_LIMIT), [allTickets]);
  const hiddenPassCount = Math.max(0, allTickets.length - DASHBOARD_PASS_PREVIEW_LIMIT);
  const hasTickets = allTickets.length > 0;

  return (
    <div className="udv2-tab-content fade-in">
      <div className="udv2-hero">
        <div className="udv2-hero-text">
          <h1 className="udv2-title">Hi, {firstName}</h1>
          <p className="udv2-subtitle">Access your tickets, orders, and affiliate rewards — all in one place.</p>
          {canAccessAdmin && (
            <button className="udv2-link udv2-admin-link" onClick={onViewAdmin}>
              Open Admin Dashboard <ChevronRight />
            </button>
          )}
        </div>
        <div className="udv2-quick-actions">
          <button className="udv2-cta" onClick={onViewTickets}>
            <TicketIcon /> View tickets
          </button>
          <button className="udv2-cta secondary" onClick={onViewOrders}>
            <OrderIcon /> View orders
          </button>
          <button className="udv2-cta secondary" onClick={onViewTransfers}>
            <TransferIcon /> Transfer history
          </button>
          <button className="udv2-cta secondary" onClick={onViewAffiliate}>
            <AffiliateIcon /> Affiliate
          </button>
          <button className="udv2-cta secondary" onClick={onViewPayment}>
            <CardIcon /> Payment
          </button>
          <button className="udv2-cta secondary" onClick={onViewAccount}>
            <AccountIcon /> Account
          </button>
          {canAccessAdmin && (
            <button className="udv2-cta secondary" onClick={onViewAdmin}>
              <DashboardIcon /> Admin dashboard
            </button>
          )}
          <button className="udv2-cta secondary udv2-logout-cta" onClick={onLogout}>
            <LogoutIcon /> Log out
          </button>
        </div>
      </div>

      <div className="udv2-stats-grid">
        {stats.map((s) => (
          <div key={s.label} className="udv2-stat-card">
            <div className="udv2-stat-value">{s.value}</div>
            <div className="udv2-stat-label">{s.label}</div>
            <div className="udv2-stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {hasTickets && (
        <section className="udv2-section">
          <div className="udv2-section-header">
            <h2>Your passes</h2>
            <button className="udv2-link" onClick={onViewTickets}>See all <ChevronRight /></button>
          </div>
          <div className="udv2-ticket-grid mini">
            {previewTickets.map((t) => (
              <TicketCard key={t.id || t.ticket_code} ticket={t} onClick={() => onSelectTicket(t)} />
            ))}
          </div>

          {hiddenPassCount > 0 && (
            <div className="udv2-pass-preview-footer">
              <div className="udv2-pass-preview-more">+{hiddenPassCount} more passes</div>
              <button className="udv2-link" onClick={onViewTickets}>View all in Tickets <ArrowRight /></button>
            </div>
          )}

          <div className="udv2-pass-info-card">
            <h3 className="udv2-pass-info-title">Festival Pass Information</h3>
            <ul className="udv2-pass-info-list">
              <li>1-Day Pass allows one person to attend one festival day.</li>
              <li>2-Day Pass allows one person to attend any two festival days.</li>
              <li>3-Day Pass allows one person to attend all festival days.</li>
              <li>Every pass is for one person only (1-Day, 2-Day, and 3-Day). If you want a friend to attend, you can buy an additional pass or transfer one of your separate passes to them.</li>
              <li>Each pass now contains a single QR code that automatically validates access for the appropriate festival days.</li>
              <li>Your QR code can only be used once per valid event day.</li>
            </ul>
            <div className="udv2-pass-info-divider" />
            <h4 className="udv2-pass-info-subtitle">PAC AMP Ticket</h4>
            <p className="udv2-pass-info-note">Your PAC AMP ticket also includes complimentary admission to the festival on the same day as your PAC AMP ticket only.</p>
          </div>
        </section>
      )}

      {recentOrders.length > 0 && (
        <section className="udv2-section">
          <div className="udv2-section-header">
            <h2>Recent orders</h2>
            <button className="udv2-link" onClick={onViewOrders}>See all <ChevronRight /></button>
          </div>
          <div className="udv2-list">
            {recentOrders.map((o) => (
              <button key={o.id || o.order_number} className="udv2-list-row udv2-list-clickable" onClick={() => onSelectOrder(o)}>
                <div>
                  <div className="udv2-list-title">{o.order_number}</div>
                  <div className="udv2-list-meta">{formatDate(o.created_at)} · {o.items?.length || 0} items</div>
                </div>
                <div className="udv2-list-end">
                  <span className={`udv2-pill ${statusColor(o.status)}`}>{o.status}</span>
                  <span className="udv2-list-amount">{formatCents(o.total_cents)}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function TicketsTab({ tickets, allTickets, filter, setFilter, onSelect, onBack }) {
  const counts = useMemo(() => {
    const c = { all: allTickets.length };
    TICKET_TABS.forEach((k) => { if (k !== 'all') c[k] = 0; });
    allTickets.forEach((t) => { c[t._group] = (c[t._group] || 0) + 1; });
    return c;
  }, [allTickets]);

  const visibleTabs = useMemo(() => {
    const active = TICKET_TABS.filter((k) => k !== 'all' && (counts[k] || 0) > 0);
    if (allTickets.length === 0) return TICKET_TABS;
    return ['all', ...active];
  }, [counts, allTickets.length]);

  return (
    <div className="udv2-tab-content fade-in">
      <BackButton onClick={onBack} />
      <div className="udv2-page-header">
        <h2>Tickets & passes</h2>
        <p>{tickets.length} active passes</p>
      </div>

      <div className="udv2-chips">
        {visibleTabs.map((k) => (
          <button
            key={k}
            className={`udv2-chip ${filter === k ? 'active' : ''}`}
            onClick={() => setFilter(k)}
          >
            {getCategoryLabel(k)}
            <span className="udv2-chip-count">{counts[k] || 0}</span>
          </button>
        ))}
      </div>

      {tickets.length === 0 ? (
        <div className="udv2-empty">
          <TicketIcon />
          <p>No tickets found in this category.</p>
          <button className="udv2-cta" onClick={() => window.location.href = '/tickets'}>Get tickets</button>
        </div>
      ) : (
        <div className="udv2-ticket-grid">
          {tickets.map((t) => (
            <TicketCard key={t.id || t.ticket_code} ticket={t} onClick={() => onSelect(t)} />
          ))}
        </div>
      )}
    </div>
  );
}

function TicketCard({ ticket, onClick }) {
  const status = String(ticket.status || '').toUpperCase();
  const seatData = getAmphitheaterSeatData(ticket);
  const seat = seatData ? formatSeatLabel(seatData, true) : null;
  const title = getTicketDisplayType(ticket);
  const isUsed = status === 'USED';
  const isVoided = VOIDED_STATUSES.has(status);
  const isActive = status === 'ISSUED';
  const category = getCategoryLabel(ticket._group);

  return (
    <button className={`udv2-ticket-card ${isUsed ? 'used' : ''}`} onClick={onClick}>
      <div className="udv2-ticket-card-inner">
        <div className="udv2-ticket-top">
          <span className="udv2-ticket-badge">{category}</span>
          <span className={`udv2-ticket-status-pill ${statusColor(ticket.status)}`}>{ticket.display_status || ticket.status}</span>
        </div>
        <div className="udv2-ticket-main">
          <div className="udv2-ticket-qr-wrap">
            {/* Card list stays calm: the classic plain QR (its token still
                rotates silently — screenshots die without the theatrics). */}
            {isActive ? (
              <RotatingQrImg ticket={ticket} size={112} alt="QR" style={{ borderRadius: 8 }} />
            ) : ticket.qr_code ? (
              <img src={ticket.qr_code} alt="QR" className={`${isVoided || isUsed ? 'dim' : ''}`} />
            ) : (
              <QrIcon />
            )}
            {isUsed && <div className="udv2-ticket-card-stamp">USED</div>}
            {isVoided && <div className="udv2-ticket-card-stamp">VOIDED</div>}
          </div>
          <div className="udv2-ticket-details">
            <div className="udv2-ticket-title-lg">{title}</div>
            {seat && <div className="udv2-ticket-seat-lg">{seat}</div>}
          </div>
        </div>
        <div className="udv2-ticket-perforation" />
        <div className="udv2-ticket-bottom">
          <span className="udv2-ticket-code-lg">{ticket.ticket_code}</span>
          <span className="udv2-ticket-view-btn">View <ChevronRight /></span>
        </div>
      </div>
    </button>
  );
}

function TicketModal({ ticket, onClose, onRefresh, googleWalletEnabled }) {
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferLabel, setTransferLabel] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState('');
  const [, setWalletAdded] = useState(Boolean(ticket.wallet_added));
  const [copied, setCopied] = useState(false);

  const status = String(ticket.status || '').toUpperCase();
  const isActive = status === 'ISSUED';
  const isUsed = status === 'USED';
  const isVoided = VOIDED_STATUSES.has(status);
  const title = getTicketDisplayType(ticket);
  const festivalValidity = getFestivalValidityDescription(ticket);
  const eventDate = getTicketEventDate(ticket);
  const isAmp = ticket?._group === 'amp';
  const validDaysText = formatValidDaysForDisplay(ticket.valid_days);
  const seatData = getAmphitheaterSeatData(ticket);
  const seat = seatData ? formatSeatLabel(seatData, false) : null;

  const handleCopy = () => {
    navigator.clipboard?.writeText(ticket.ticket_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    const email = transferEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setTransferLoading(true);
    try {
      const res = await api.createTransfer(ticket.id, email, transferLabel.trim());
      if (res?.success) {
        toast.success('Transfer sent');
        setTransferOpen(false);
        setTransferEmail('');
        setTransferLabel('');
        onClose();
        onRefresh();
      } else {
        toast.error(res?.error?.message || 'Transfer failed');
      }
    } catch (err) {
      toast.error(err?.message || 'Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleWallet = async (type) => {
    if (!isActive) return;
    setWalletLoading(type);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const walletWindow = type === 'apple'
      ? (isIOS ? null : window.open('about:blank', '_blank'))
      : window.open('about:blank', '_blank');
    try {
      const res = type === 'apple'
        ? await api.createAppleWalletPass(ticket.id)
        : await api.createGoogleWalletPass(ticket.id);

      if (res?.inAppBrowser) {
        if (walletWindow && !walletWindow.closed) walletWindow.close();
        toast.error(res.error?.message || 'Open in Safari to add to Apple Wallet.');
        return;
      }

      if (res?.success) {
        setWalletAdded(true);
        const url = type === 'apple' ? res.download_url : (res.save_url || res.add_url);
        if (url) {
          if (isIOS && type === 'apple') {
            window.location.href = url;
          } else if (walletWindow && !walletWindow.closed) {
            walletWindow.location.href = url;
          } else {
            window.location.href = url;
          }
        }
      } else {
        if (walletWindow && !walletWindow.closed) walletWindow.close();
        toast.error(res?.error?.message || 'Wallet pass failed');
      }
    } catch (err) {
      if (walletWindow && !walletWindow.closed) walletWindow.close();
      toast.error(err?.message || 'Wallet pass failed');
    } finally {
      setWalletLoading('');
    }
  };

  const category = getCategoryLabel(ticket._group);

  return (
    <div className="udv2-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="udv2-modal udv2-ticket-modal">
        <button className="udv2-modal-close" onClick={onClose} aria-label="Close"><CloseIcon /></button>

        <div className="udv2-ticket-modal-inner">
          <div className="udv2-ticket-modal-top">
            <span className="udv2-ticket-badge">{category}</span>
            <span className={`udv2-ticket-status-pill ${statusColor(ticket.status)}`}>{ticket.display_status || ticket.status}</span>
          </div>

          <div className="udv2-modal-header">
            <h2>{title}</h2>
            {seat && <p>{seat}</p>}
          </div>

        {seatData && (
          <div className="udv2-seat-summary">
            <div className="udv2-seat-summary-item">
              <span>Section</span>
              <strong>{seatData.section || '—'}</strong>
            </div>
            <div className="udv2-seat-summary-item">
              <span>Row</span>
              <strong>{seatData.row || '—'}</strong>
            </div>
            <div className="udv2-seat-summary-item">
              <span>Seat</span>
              <strong>{seatData.seat || '—'}</strong>
            </div>
          </div>
        )}

        <div className="udv2-qr-stage">
          <AnimatedQrFrame ticket={ticket} size={232}>
            <div className="udv2-qr-inner" style={{ width: 232, height: 232 }}>
              {isActive ? (
                <RotatingQrImg ticket={ticket} size={232} alt="Ticket QR" />
              ) : ticket.qr_code ? (
                <img src={ticket.qr_code} alt="Ticket QR" className={`${isVoided || isUsed ? 'dim' : ''}`} />
              ) : (
                <div className="udv2-qr-placeholder"><QrIcon /></div>
              )}
              {isUsed && <div className="udv2-qr-stamp">USED</div>}
              {isVoided && <div className="udv2-qr-stamp">VOIDED</div>}
            </div>
          </AnimatedQrFrame>
          <button className="udv2-copy-code" onClick={handleCopy}>
            <CopyIcon /> {copied ? 'Copied' : ticket.ticket_code}
          </button>
        </div>

        <div className="udv2-detail-grid">
          <Detail label="Status" value={ticket.display_status || ticket.status} />
          {festivalValidity && <Detail label="Valid days" value={festivalValidity} />}
          {!festivalValidity && isAmp && eventDate && <Detail label="Event date" value={formatDate(eventDate)} />}
          {!festivalValidity && !isAmp && validDaysText && <Detail label="Valid days" value={validDaysText} />}
          {!festivalValidity && !validDaysText && !isAmp && eventDate && <Detail label="Event date" value={formatDate(eventDate)} />}
          {ticket.order_number && <Detail label="Order" value={ticket.order_number} />}
        </div>

        {isActive && (
          <div className="udv2-wallet-row">
            <button
              className="udv2-wallet-badge-btn"
              onClick={() => handleWallet('apple')}
              disabled={walletLoading === 'apple' || walletLoading === 'google'}
              aria-label="Add to Apple Wallet"
            >
              {walletLoading === 'apple' ? (
                <span className="udv2-wallet-loading">Creating…</span>
              ) : (
                <img src="/apple.webp" alt="Add to Apple Wallet" className="udv2-wallet-badge-img" />
              )}
            </button>
            {(typeof googleWalletEnabled === 'undefined' || googleWalletEnabled) && (
              <button
                className="udv2-wallet-badge-btn"
                onClick={() => handleWallet('google')}
                disabled={walletLoading === 'apple' || walletLoading === 'google'}
                aria-label="Add to Google Wallet"
              >
                {walletLoading === 'google' ? (
                  <span className="udv2-wallet-loading">Creating…</span>
                ) : (
                  <img src="/enUS_add_to_google_wallet_add-wallet-badge.svg" alt="Add to Google Wallet" className="udv2-wallet-badge-img" />
                )}
              </button>
            )}
          </div>
        )}

        {isActive && (
          <div className="udv2-transfer">
            {!transferOpen ? (
              <button className="udv2-cta secondary full" onClick={() => setTransferOpen(true)}>
                Transfer ticket
              </button>
            ) : (
              <form onSubmit={handleTransfer} className="udv2-transfer-form">
                <input
                  type="text"
                  placeholder="Gift label (optional)"
                  value={transferLabel}
                  onChange={(e) => setTransferLabel(e.target.value)}
                  maxLength={60}
                />
                <input
                  type="email"
                  required
                  placeholder="Recipient email"
                  value={transferEmail}
                  onChange={(e) => setTransferEmail(e.target.value)}
                />
                <div className="udv2-transfer-actions">
                  <button type="button" className="udv2-cta ghost" onClick={() => setTransferOpen(false)}>Cancel</button>
                  <button type="submit" className="udv2-cta" disabled={transferLoading}>
                    {transferLoading ? 'Sending...' : 'Send ticket'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="udv2-detail">
      <div className="udv2-detail-label">{label}</div>
      <div className="udv2-detail-value">{value}</div>
    </div>
  );
}

function OrderModal({ order, currentUserEmail, onClose }) {
  const invoiceUrl = api.getInvoicePdfUrl(order.id);
  const hasInvoice = order.status && String(order.status).toUpperCase() !== 'PENDING';
  const ampSeats = normalizeOrderAmpSeats(order);
  const itemCount = Number(order.total_item_quantity || 0) || (order.items?.length || 0);
  const orderEmail = order?.buyer?.email || order?.email || currentUserEmail || '—';

  return (
    <div className="udv2-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="udv2-modal udv2-order-modal">
        <button className="udv2-modal-close" onClick={onClose} aria-label="Close"><CloseIcon /></button>

        <div className="udv2-modal-header">
          <h2>{order.order_number}</h2>
          <p>{formatDate(order.created_at)} · {formatCents(order.total_cents)}</p>
        </div>

        <div className="udv2-detail-grid">
          <Detail label="Status" value={order.status} />
          <Detail label="Payment" value={order.payment_method || 'Stripe'} />
          <Detail label="Email" value={orderEmail} />
          <Detail label="Items" value={itemCount} />
        </div>

        <h3 className="udv2-section-subtitle">What you purchased</h3>
        <div className="udv2-list udv2-order-items">
          {(order.items || []).map((item, idx) => (
            <div key={idx} className="udv2-list-row">
              <div>
                <div className="udv2-list-title">{item.ticket_type_name || item.name || 'Item'}</div>
                {item.ticket_holder_name && <div className="udv2-list-meta">{item.ticket_holder_name}</div>}
                {isAmphitheaterOrderItem(item) && ampSeats.length > 0 && (
                  <div className="udv2-list-meta">{ampSeats.length} Pacific Amphitheatre seat{ampSeats.length > 1 ? 's' : ''} listed below</div>
                )}
              </div>
              <div className="udv2-list-end">
                <span className="udv2-list-amount">x{item.quantity || 1}</span>
                <span className="udv2-list-amount">{formatCents(item.price_cents || item.total_cents || 0)}</span>
              </div>
            </div>
          ))}
        </div>

        {ampSeats.length > 0 && (
          <>
            <h3 className="udv2-section-subtitle">Pacific Amphitheatre Seats</h3>
            <div className="udv2-list udv2-order-seat-list">
              {ampSeats.map((seat, idx) => (
                <div key={`${seat.event_date || 'na'}-${seat.section || 'na'}-${seat.row || 'na'}-${seat.seat_number || 'na'}-${idx}`} className="udv2-list-row">
                  <div>
                    <div className="udv2-list-title">{seat.display}</div>
                    {seat.event_date && <div className="udv2-list-meta">{formatDate(seat.event_date)}</div>}
                  </div>
                  <div className="udv2-list-end">
                    <span className="udv2-list-amount">#{idx + 1}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="udv2-order-actions">
          {hasInvoice && (
            <a className="udv2-cta secondary" href={invoiceUrl} target="_blank" rel="noopener noreferrer">Download invoice</a>
          )}
        </div>
      </div>
    </div>
  );
}

function OrdersTab({ orders, onSelect, onBack }) {
  return (
    <div className="udv2-tab-content fade-in">
      <BackButton onClick={onBack} />
      <div className="udv2-page-header"><h2>Orders</h2><p>{orders.length} orders</p></div>
      {orders.length === 0 ? (
        <div className="udv2-empty"><OrderIcon /><p>No orders yet.</p><button className="udv2-cta" onClick={() => window.location.href = '/tickets'}>Shop tickets</button></div>
      ) : (
        <div className="udv2-list">
          {orders.map((o) => (
            <button key={o.id || o.order_number} className="udv2-list-row udv2-list-clickable" onClick={() => onSelect(o)}>
              <div>
                <div className="udv2-list-title">{o.order_number}</div>
                <div className="udv2-list-meta">{formatDate(o.created_at)} · {Number(o.total_item_quantity || 0) || (o.items?.length || 0)} items · {o.payment_method || 'Stripe'}</div>
                {normalizeOrderAmpSeats(o).length > 0 && (
                  <div className="udv2-order-seat-preview-list">
                    {normalizeOrderAmpSeats(o).map((seat, idx) => (
                      <div key={`${o.id || o.order_number}-${idx}`} className="udv2-order-seat-preview-row">
                        {seat.display}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="udv2-list-end">
                <span className={`udv2-pill ${statusColor(o.status)}`}>{o.status}</span>
                <span className="udv2-list-amount">{formatCents(o.total_cents)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AffiliateTab({ affiliate, payouts, onBack }) {
  const [zelleEmail, setZelleEmail] = useState(affiliate?.zelle_email || '');
  const [zellePhone, setZellePhone] = useState(affiliate?.zelle_phone || '');
  const [zelleEmailSaving, setZelleEmailSaving] = useState(false);
  const [zelleEmailMsg, setZelleEmailMsg] = useState({ type: '', text: '' });
  const [referralLinkCopied, setReferralLinkCopied] = useState(false);
  const [affiliateOrdersPage, setAffiliateOrdersPage] = useState(1);

  useEffect(() => {
    setZelleEmail(affiliate?.zelle_email || '');
    setZellePhone(affiliate?.zelle_phone || '');
  }, [affiliate?.zelle_email, affiliate?.zelle_phone]);

  const allOrders = useMemo(() => affiliate?.orders || [], [affiliate]);
  const periods = useMemo(() => {
    const periodMap = {};
    allOrders.forEach((o) => {
      const pd = o.payout_date || 'Unknown';
      if (!periodMap[pd]) periodMap[pd] = { commission: 0, count: 0, paid: null };
      periodMap[pd].commission += (o.commission_cents || 0);
      periodMap[pd].count += 1;
      if (o.date_paid && !periodMap[pd].paid) periodMap[pd].paid = o.date_paid;
    });
    return Object.entries(periodMap).sort((a, b) => a[0].localeCompare(b[0]));
  }, [allOrders]);

  if (!affiliate?.coupon_code) {
    return (
      <div className="udv2-tab-content fade-in">
        <div className="udv2-page-header"><h2>Affiliate Program</h2></div>
        <div className="udv2-empty"><AffiliateIcon /><p>You are not enrolled in the affiliate program yet.</p></div>
      </div>
    );
  }

  const AFFILIATE_ORDERS_PER_PAGE = 25;
  const affiliateInfo = affiliate;

  const formatZellePhoneInput = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const getReferralLink = () => `${window.location.origin}/?ref=${affiliateInfo.coupon_code}`;

  const copyReferralLink = () => {
    const link = getReferralLink();
    if (!link) return;
    navigator.clipboard.writeText(link);
    setReferralLinkCopied(true);
    setTimeout(() => setReferralLinkCopied(false), 2500);
  };

  const getNextPayoutDate = () => {
    const now = new Date();
    let nextDate;
    if (now.getDate() <= 15) {
      nextDate = new Date(now.getFullYear(), now.getMonth(), 15);
    } else {
      nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }
    if (nextDate <= now) {
      if (now.getDate() <= 15) {
        nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      } else {
        nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 15);
      }
    }
    return nextDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const saveZelleDetails = async () => {
    setZelleEmailSaving(true);
    setZelleEmailMsg({ type: '', text: '' });
    const phoneDigits = zellePhone.replace(/\D/g, '');
    if (zellePhone && phoneDigits.length !== 10) {
      setZelleEmailMsg({ type: 'error', text: 'Please enter a valid 10-digit phone number.' });
      setZelleEmailSaving(false);
      return;
    }
    try {
      const res = await api.updateMyAffiliateInfo({ zelle_email: zelleEmail, zelle_phone: zellePhone });
      if (res?.success) {
        setZelleEmailMsg({ type: 'success', text: 'Zelle details saved!' });
      } else {
        setZelleEmailMsg({ type: 'error', text: res?.error?.message || 'Failed to save' });
      }
    } catch (err) {
      setZelleEmailMsg({ type: 'error', text: 'Failed to save Zelle details' });
    } finally {
      setZelleEmailSaving(false);
    }
  };

  const totalOrders = allOrders.length;
  const totalPages = Math.ceil(totalOrders / AFFILIATE_ORDERS_PER_PAGE);
  const startIdx = (affiliateOrdersPage - 1) * AFFILIATE_ORDERS_PER_PAGE;
  const pageOrders = allOrders.slice(startIdx, startIdx + AFFILIATE_ORDERS_PER_PAGE);

  return (
    <div className="udv2-tab-content fade-in">
      <BackButton onClick={onBack} />
      <div className="udv2-page-header"><h2>Affiliate Program</h2></div>

      <div className="udv2-affiliate-program-card">
        <h3 className="udv2-affiliate-program-title">Affiliate Program</h3>
        <div className="udv2-affiliate-program-body">
          <p className="udv2-affiliate-program-line">
            Your Promo Code: <span className="udv2-affiliate-program-code">{affiliateInfo.coupon_code}</span>
            <button
              className="udv2-icon-copy"
              title="Copy coupon code"
              onClick={() => { navigator.clipboard.writeText(affiliateInfo.coupon_code); toast.success('Code copied'); }}
            >
              <CopyIcon />
            </button>
          </p>
          <p className="udv2-affiliate-program-meta">
            {affiliateInfo.commission_percent}% commission · {(affiliateInfo.discount_percent !== null && affiliateInfo.discount_percent !== undefined) ? affiliateInfo.discount_percent : 10}% off for customers (any ticket) · {(affiliateInfo.total_orders || 0).toLocaleString()} order(s) · ${Number(affiliateInfo.total_commission || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} earned
          </p>
          <div className="udv2-affiliate-share">
            <span>Referral Link:</span>
            <code className="udv2-affiliate-share-url">{getReferralLink()}</code>
            <button className="udv2-affiliate-share-btn" onClick={copyReferralLink}>
              {referralLinkCopied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      </div>

      <div className="udv2-affiliate-top-row">
        <div className="udv2-affiliate-status-card">
          <p>Status</p>
          <p className={affiliateInfo.is_active ? 'udv2-affiliate-active' : 'udv2-affiliate-inactive'}>
            {affiliateInfo.is_active ? 'ACTIVE' : 'INACTIVE'}
          </p>
        </div>
        <div className="udv2-affiliate-status-card">
          <p>Coupon</p>
          <p className="udv2-affiliate-coupon">
            {affiliateInfo.coupon_code}
            <button
              className="udv2-icon-copy"
              title="Copy coupon code"
              onClick={() => { navigator.clipboard.writeText(affiliateInfo.coupon_code); }}
            >
              <CopyIcon />
            </button>
          </p>
        </div>
        <div className="udv2-affiliate-status-card">
          <p>Payout</p>
          <p>{getNextPayoutDate()}</p>
        </div>
      </div>

      <div className="udv2-affiliate-stats-grid">
        {[
          { label: 'Orders', value: (affiliateInfo.total_orders || 0).toLocaleString() },
          { label: 'Festival Tickets', value: (affiliateInfo.festival_tickets || 0).toLocaleString() },
          { label: 'Concert Tickets', value: (affiliateInfo.concert_tickets || 0).toLocaleString() },
          { label: 'Commission Earned', value: `$${Number(affiliateInfo.total_commission || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
        ].map((stat, i) => (
          <div key={i} className="udv2-affiliate-stat-box">
            <div className="udv2-affiliate-stat-value">{stat.value}</div>
            <div className="udv2-affiliate-stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {periods.length > 1 && (
        <div className="udv2-affiliate-section">
          <h4>Commission by Period</h4>
          <div className="udv2-affiliate-period-grid">
            {periods.map(([pd, data]) => {
              const label = pd !== 'Unknown' ? new Date(pd + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Unknown';
              const isPaid = !!data.paid;
              return (
                <div key={pd} className={`udv2-affiliate-period-card ${isPaid ? 'paid' : 'pending'}`}>
                  <p className="udv2-affiliate-period-label">Payout {label}</p>
                  <p className="udv2-affiliate-period-amount">
                    ${(data.commission / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="udv2-affiliate-period-meta">
                    {data.count} order{data.count !== 1 ? 's' : ''} · <span>{isPaid ? 'Paid' : 'Pending'}</span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="udv2-affiliate-share-banner">
        <p>We've made sharing easy for you</p>
        <button className="udv2-cta secondary" onClick={copyReferralLink}>
          {referralLinkCopied ? 'Copied!' : 'Copy Referral Link'}
        </button>
      </div>

      <div className="udv2-affiliate-section">
        <h4>Provide your Zelle Email or Phone Number</h4>
        <p className="udv2-muted">Enter the email address or phone number associated with your Zelle account for timely payouts.</p>
        <div className="udv2-affiliate-zelle">
          <input
            type="email"
            placeholder="your-zelle@email.com"
            value={zelleEmail}
            onChange={(e) => { setZelleEmail(e.target.value); setZelleEmailMsg({ type: '', text: '' }); }}
          />
          <input
            type="tel"
            placeholder="(xxx) xxx-xxxx"
            value={zellePhone}
            onChange={(e) => { setZellePhone(formatZellePhoneInput(e.target.value)); setZelleEmailMsg({ type: '', text: '' }); }}
          />
          <button className="udv2-cta" onClick={saveZelleDetails} disabled={zelleEmailSaving}>
            {zelleEmailSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
        {zelleEmailMsg.text && <p className={`udv2-affiliate-zelle-msg ${zelleEmailMsg.type}`}>{zelleEmailMsg.text}</p>}
      </div>

      <div className="udv2-affiliate-section">
        <div className="udv2-section-header">
          <div>
            <h4>Orders Table</h4>
            <p className="udv2-muted">Track the customers and commission generated by your promo code.</p>
          </div>
          {totalOrders > AFFILIATE_ORDERS_PER_PAGE && (
            <span className="udv2-muted">{startIdx + 1}–{Math.min(startIdx + AFFILIATE_ORDERS_PER_PAGE, totalOrders)} of {totalOrders} orders</span>
          )}
        </div>

        {totalOrders > 0 ? (
          <>
            <div className="udv2-affiliate-table-wrap">
              <table className="udv2-affiliate-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer Name</th>
                    <th>Order #</th>
                    <th>Status</th>
                    <th>Order Total</th>
                    <th>Subtotal Revenue</th>
                    <th>Commission</th>
                    <th>Payout Date</th>
                    <th>Date Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {pageOrders.map((order, idx) => (
                    <tr key={order.id || idx}>
                      <td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : '—'}</td>
                      <td title={order.buyer_email || order.buyer_name || ''}>{order.buyer_name || '—'}</td>
                      <td><span className="udv2-affiliate-order-code">{order.order_number || '—'}</span></td>
                      <td>
                        <span className={`udv2-pill ${order.status === 'REFUNDED' ? 'danger' : order.status === 'PARTIALLY_REFUNDED' ? 'warning' : 'success'}`}>
                          {order.status === 'REFUNDED' ? 'Refunded' : order.status === 'PARTIALLY_REFUNDED' ? 'Partially Refunded' : 'Paid'}
                        </span>
                      </td>
                      <td>${((order.order_total_cents || order.total_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>${((order.revenue_cents || order.total_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className={(order.status === 'REFUNDED' || order.status === 'PARTIALLY_REFUNDED') ? 'udv2-muted' : 'udv2-affiliate-commission'}>
                        ${((order.commission_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td>{order.payout_date ? new Date(order.payout_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                      <td className={order.date_paid ? 'udv2-affiliate-paid' : 'udv2-muted'}>
                        {order.date_paid ? new Date(order.date_paid).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Pending'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="udv2-affiliate-pagination">
                <button disabled={affiliateOrdersPage === 1} onClick={() => setAffiliateOrdersPage((p) => Math.max(1, p - 1))}>Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                  <button
                    key={pg}
                    className={pg === affiliateOrdersPage ? 'active' : ''}
                    onClick={() => setAffiliateOrdersPage(pg)}
                  >{pg}</button>
                ))}
                <button disabled={affiliateOrdersPage === totalPages} onClick={() => setAffiliateOrdersPage((p) => Math.min(totalPages, p + 1))}>Next</button>
              </div>
            )}
          </>
        ) : (
          <div className="udv2-empty small"><p>No coupon orders yet.</p></div>
        )}
      </div>

      <p className="udv2-affiliate-note">*Payout is immediate, but it may take 5-10 business days to arrive in your bank account</p>

      <div className="udv2-affiliate-section">
        <h4>Payout Structure</h4>
        <p className="udv2-muted">Payouts are processed bi-weekly — 2 payout periods per month.</p>
        <div className="udv2-affiliate-table-wrap">
          <table className="udv2-affiliate-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Covers Orders From</th>
                <th>Payout Date</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1st Period</td>
                <td>1st – 15th of the month</td>
                <td>On or around the 15th</td>
              </tr>
              <tr>
                <td>2nd Period</td>
                <td>16th – Last day of the month</td>
                <td>On or around the last day</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="udv2-affiliate-section">
        <h4>Payout History</h4>
        {payouts.length > 0 ? (
          <div className="udv2-affiliate-table-wrap">
            <table className="udv2-affiliate-table">
              <thead>
                <tr>
                  <th>Payout Period</th>
                  <th>Payout Date</th>
                  <th>Date Paid</th>
                  <th>Total Payout</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id}>
                    <td>{new Date(p.period_start + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(p.period_end + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td>{new Date(p.period_end + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</td>
                    <td className={p.paid_at ? 'udv2-affiliate-paid' : 'udv2-muted'}>{p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</td>
                    <td className="udv2-affiliate-commission">${Number(p.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="udv2-empty small"><p>No payouts yet. Payouts are processed bi-weekly on the 15th and last day of each month.</p></div>
        )}
      </div>
    </div>
  );
}

function PaymentTab({ card, setCard, onBack }) {
  const [removing, setRemoving] = useState(false);

  const removeCard = async () => {
    if (!window.confirm('Remove saved card?')) return;
    setRemoving(true);
    try {
      const res = await api.removeSavedCard();
      if (res?.success) {
        setCard(null);
        toast.success('Card removed');
      } else {
        toast.error(res?.error?.message || 'Failed to remove card');
      }
    } catch (err) {
      toast.error('Failed to remove card');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="udv2-tab-content fade-in">
      <BackButton onClick={onBack} />
      <div className="udv2-page-header"><h2>Payment method</h2></div>
      {card ? (
        <div className="udv2-card">
          <div className="udv2-card-row">
            <span className="udv2-card-brand">{card.brand?.toUpperCase() || 'CARD'}</span>
            <span className="udv2-card-ending">•••• {card.last4}</span>
          </div>
          <div className="udv2-card-row">
            <span className="udv2-muted">Expires {card.exp_month || '—'}/{card.exp_year || '—'}</span>
            <button className="udv2-text-danger" onClick={removeCard} disabled={removing}>{removing ? 'Removing...' : 'Remove'}</button>
          </div>
        </div>
      ) : (
        <div className="udv2-empty"><CardIcon /><p>No saved card on file.</p><p className="udv2-muted">Your card is saved at checkout for faster purchases.</p></div>
      )}
    </div>
  );
}

function AccountTab({ profile, setProfile, onBack }) {
  const [saving, setSaving] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [form, setForm] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    facebook: profile?.facebook || '',
    instagram: profile?.instagram || '',
    tiktok: profile?.tiktok || '',
  });
  const [emailForm, setEmailForm] = useState({
    email: profile?.email || '',
    current_password: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        facebook: profile.facebook || '',
        instagram: profile.instagram || '',
        tiktok: profile.tiktok || '',
      });
      setEmailForm({ email: profile.email || '', current_password: '' });
    }
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.updateProfile({
        full_name: form.full_name,
        phone: form.phone,
        facebook: form.facebook,
        instagram: form.instagram,
        tiktok: form.tiktok,
      });
      if (res?.success) {
        setProfile(res.data);
        toast.success('Profile updated');
      } else {
        toast.error(res?.error?.message || 'Update failed');
      }
    } catch (err) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const newEmail = emailForm.email.trim().toLowerCase();
    const currentEmail = String(profile?.email || '').trim().toLowerCase();
    if (!newEmail) {
      toast.error('Please enter a valid email');
      return;
    }
    if (newEmail === currentEmail) {
      toast.info('Please enter a different email to update');
      return;
    }
    if (!emailForm.current_password) {
      toast.error('Current password is required to change email');
      return;
    }
    setSavingEmail(true);
    try {
      const res = await api.updateProfile({
        email: newEmail,
        current_password: emailForm.current_password,
      });
      if (res?.success) {
        setProfile(res.data);
        toast.success('Email updated');
        setEmailForm({ email: res?.data?.email || newEmail, current_password: '' });
      } else {
        toast.error(res?.error?.message || 'Email update failed');
      }
    } catch (err) {
      toast.error('Email update failed');
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwordForm.current_password) {
      toast.error('Current password is required');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSavingPassword(true);
    try {
      const res = await api.changePassword(passwordForm.current_password, passwordForm.new_password);
      if (res?.success) {
        toast.success('Password changed');
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        toast.error(res?.error?.message || 'Password change failed');
      }
    } catch (err) {
      toast.error('Password change failed');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="udv2-tab-content fade-in">
      <BackButton onClick={onBack} />
      <div className="udv2-page-header"><h2>Account details</h2></div>

      <form onSubmit={handleSubmit} className="udv2-form">
        <label>
          <span>Full name</span>
          <input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
        </label>
        <label>
          <span>Phone</span>
          <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </label>
        <div className="udv2-form-section">
          <span>Social links</span>
          <input type="text" placeholder="Facebook" value={form.facebook} onChange={(e) => setForm({ ...form, facebook: e.target.value })} />
          <input type="text" placeholder="Instagram" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} />
          <input type="text" placeholder="TikTok" value={form.tiktok} onChange={(e) => setForm({ ...form, tiktok: e.target.value })} />
        </div>
        <button type="submit" className="udv2-cta" disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button>
      </form>

      <div className="udv2-form-divider" />

      <form onSubmit={handleEmailSubmit} className="udv2-form">
        <div className="udv2-form-section-title">Change email</div>
        <label>
          <span>New email</span>
          <input type="email" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} />
        </label>
        <label>
          <span>Current password (for security)</span>
          <input type="password" placeholder="Enter your password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} />
        </label>
        <button type="submit" className="udv2-cta" disabled={savingEmail}>{savingEmail ? 'Updating...' : 'Update email'}</button>
      </form>

      <div className="udv2-form-divider" />

      <form onSubmit={handlePasswordSubmit} className="udv2-form">
        <div className="udv2-form-section-title">Change password</div>
        <label>
          <span>Current password</span>
          <input type="password" placeholder="Enter current password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} />
        </label>
        <label>
          <span>New password</span>
          <input type="password" placeholder="At least 8 characters" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} />
        </label>
        <label>
          <span>Confirm new password</span>
          <input type="password" placeholder="Re-enter new password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} />
        </label>
        <button type="submit" className="udv2-cta" disabled={savingPassword}>{savingPassword ? 'Changing...' : 'Change password'}</button>
      </form>
    </div>
  );
}

const VENDOR_DOC_TYPES = [
  { key: 'SELLER_PERMIT', label: "Seller's Permit", required: true },
  { key: 'BUSINESS_LICENSE', label: 'Business License or Tax Certificate', required: true },
  { key: 'HEALTH_PERMIT', label: 'Health Permit (if applicable)', required: false },
  { key: 'FOOD_HANDLER', label: "Food Handler's License (if applicable)", required: false },
];

function getVendorDocByType(docs, docType) {
  return docs.find((d) => d.document_type === docType);
}

function getVendorDocStatus(docs, docType, isRequired) {
  const doc = getVendorDocByType(docs, docType);
  if (!doc) return { status: 'missing', label: 'Not uploaded', class: 'neutral' };
  switch (doc.validation_status) {
    case 'APPROVED': return { status: 'approved', label: 'Approved', class: 'success' };
    case 'REJECTED': return { status: 'rejected', label: 'Rejected', class: 'danger' };
    case 'EXPIRED': return { status: 'expired', label: 'Expired', class: 'warning' };
    default: return { status: 'uploaded', label: 'Uploaded', class: 'success' };
  }
}

function VendorTab({ vendorOrderInfo, vendorDocuments, uploading, docError, docSuccess, signing, onUpload, onSignContract, onBack }) {
  const fileInputRefs = useRef({});
  const contractDoc = getVendorDocByType(vendorDocuments, 'CONTRACT');
  const contractSigned = contractDoc?.is_signed;
  const vendorIsPending = vendorOrderInfo?.status === 'PENDING';
  const vendorIsRejected = vendorOrderInfo?.status === 'REJECTED';

  const requiredDocs = VENDOR_DOC_TYPES.filter((d) => d.required);
  const allDocsApproved = contractSigned && requiredDocs.every((d) => getVendorDocStatus(vendorDocuments, d.key, true).status === 'approved');

  const handleFileChange = (docType, e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(docType, file);
    e.target.value = '';
  };

  const statusConfig = {
    PENDING: { label: 'PENDING', bg: '#FDFBE7', color: '#78350F' },
    APPROVED: { label: 'APPROVED!', bg: '#EFFCF3', color: '#15803D' },
    REJECTED: { label: 'REJECTED', bg: '#FDF1F1', color: '#B81C1C' },
  };
  const cfg = statusConfig[vendorOrderInfo?.status] || statusConfig.PENDING;

  return (
    <div className="udv2-tab-content fade-in">
      <BackButton onClick={onBack} />
      <div className="udv2-page-header">
        <h2>Vendor Status & Documents</h2>
        <p>{vendorOrderInfo?.businessName || 'Vendor application'}</p>
      </div>

      {/* Approval Status Card */}
      {vendorOrderInfo && (
        <div className="udv2-vendor-status-card">
          <h3 className="udv2-vendor-status-title">Vendor Approval Status</h3>
          <div className="udv2-vendor-status-banner" style={{ background: cfg.bg }}>
            <div className="udv2-vendor-status-label" style={{ color: cfg.color }}>{cfg.label}</div>
            <div className="udv2-vendor-status-business" style={{ color: cfg.color }}>{vendorOrderInfo.businessName}</div>
            <div className="udv2-vendor-status-order">Order #{vendorOrderInfo.orderNumber}</div>
            <div className="udv2-vendor-status-booth" style={{ color: cfg.color }}>{vendorOrderInfo.boothLabel}</div>
          </div>
          <div className="udv2-vendor-status-body">
            {vendorOrderInfo.status === 'PENDING' && (
              <>
                <p>You will receive an email within 1-2 business days!</p>
                <p>Please check your spam/junk folder</p>
              </>
            )}
            {vendorOrderInfo.status === 'APPROVED' && !contractSigned && (
              <button
                className="udv2-cta udv2-vendor-sign-btn"
                onClick={onSignContract}
                disabled={signing}
              >
                {signing ? 'Loading...' : 'Sign Vendor Agreement'}
              </button>
            )}
            {vendorOrderInfo.status === 'REJECTED' && (
              <div className="udv2-vendor-rejected-msg">
                <p>Hi {vendorOrderInfo.vendorName},</p>
                <p>Thank you for your interest in participating in OC MENA Festival 2026.</p>
                <p>After reviewing all submissions, we regret to inform you that we are unable to approve your vendor application at this time.</p>
                <p>Vendor participation is subject to review and approval to maintain a balanced and diverse marketplace. In some cases, we receive multiple applications within the same category and must limit participation accordingly.</p>
                <p>Per our vendor policy, we reserve the right to approve or decline applications at our discretion.</p>
                <p>A full refund of your booth payment will be processed within 5-10 business days to your original payment method.</p>
                <p>We truly appreciate your interest and encourage you to apply for future events.</p>
                <p>Sincerely,<br />OC MENA Festival Team</p>
              </div>
            )}
            <div className="udv2-vendor-contact">
              <p className="udv2-vendor-contact-title">For more information:</p>
              <p>razan@ocmenafestival.com</p>
              <p>(949) 208-7791</p>
            </div>
          </div>
        </div>
      )}

      {/* Documents section — only show when approved or already has docs */}
      {vendorOrderInfo && !vendorIsRejected && (
        <div className="udv2-vendor-docs-section">
          <h3 className="udv2-vendor-docs-heading">Vendor Documents</h3>

          {allDocsApproved && (
            <div className="udv2-vendor-alert udv2-vendor-alert-success">
              All required documents approved! You're all set for the festival.
            </div>
          )}
          {docError && (
            <div className="udv2-vendor-alert udv2-vendor-alert-error">{docError}</div>
          )}
          {docSuccess && (
            <div className="udv2-vendor-alert udv2-vendor-alert-info">{docSuccess}</div>
          )}

          {/* Contract row */}
          <div className="udv2-vendor-doc-card">
            <div className="udv2-vendor-doc-header">
              <div className="udv2-vendor-doc-label">
                <DocIcon /> Contract
              </div>
              <div className="udv2-vendor-doc-actions">
                <button
                  className="udv2-vendor-doc-btn"
                  onClick={onSignContract}
                  disabled={contractSigned || signing || vendorIsPending}
                >
                  {signing ? 'Signing...' : contractSigned ? 'Contract Signed' : vendorIsPending ? 'Sign Contract (after approval)' : 'Sign Contract'}
                </button>
                <span className={`udv2-vendor-status-pill ${contractSigned ? 'success' : 'danger'}`}>
                  {contractSigned ? 'Signed' : 'Not signed'}
                </span>
                {contractSigned && contractDoc?.file_url && (
                  <a className="udv2-vendor-doc-download" href={contractDoc.file_url} target="_blank" rel="noopener noreferrer">
                    Download
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Permit note */}
          <div className="udv2-vendor-doc-note">
            <strong>Permits:</strong> Permit documentation is due a minimum of 14 days prior to the event. No refunds will be issued for late or missing submissions.
          </div>

          {/* Vendor extra info */}
          {(vendorOrderInfo.licensePlateNumber || vendorOrderInfo.productType) && (
            <div className="udv2-vendor-extra-info">
              {vendorOrderInfo.licensePlateNumber && (
                <div>
                  <span className="udv2-vendor-extra-label">License Plate</span>
                  <div className="udv2-vendor-extra-value">{vendorOrderInfo.licensePlateNumber}</div>
                </div>
              )}
              {vendorOrderInfo.productType && (
                <div>
                  <span className="udv2-vendor-extra-label">Product Type</span>
                  <div className="udv2-vendor-extra-value">
                    {vendorOrderInfo.productType === 'prepackaged' ? 'Prepackaged' :
                     vendorOrderInfo.productType === 'prepackaged_sampling' ? 'Prepackaged w/ Sampling' :
                     vendorOrderInfo.productType === 'unpackaged' ? 'Unpackaged/Open Food' :
                     vendorOrderInfo.productType}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Upload rows */}
          <div className="udv2-vendor-upload-list">
            {VENDOR_DOC_TYPES.map((docType) => {
              const doc = getVendorDocByType(vendorDocuments, docType.key);
              const status = getVendorDocStatus(vendorDocuments, docType.key, docType.required);
              const isUploading = uploading[docType.key];
              return (
                <div key={docType.key} className="udv2-vendor-upload-row">
                  <div className="udv2-vendor-upload-label">
                    {docType.label}
                    {!docType.required && <span className="udv2-vendor-optional"> (optional)</span>}
                  </div>
                  <div className="udv2-vendor-upload-control">
                    <input
                      ref={(el) => { fileInputRefs.current[docType.key] = el; }}
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                      onChange={(e) => handleFileChange(docType.key, e)}
                      disabled={isUploading}
                      style={{ display: 'none' }}
                    />
                    <button
                      className="udv2-vendor-upload-display"
                      onClick={() => fileInputRefs.current[docType.key]?.click()}
                      disabled={isUploading}
                    >
                      <UploadIcon />
                      <span>{isUploading ? 'Uploading...' : doc?.original_filename || 'Click to upload file'}</span>
                    </button>
                    {doc && (
                      <a
                        className="udv2-vendor-doc-download"
                        href={api.getVendorDocumentDownloadUrl(doc.id)}
                        onClick={(e) => e.stopPropagation()}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download
                      </a>
                    )}
                  </div>
                  <span className={`udv2-vendor-status-pill ${status.class}`}>{status.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
